import { flexRender, Table } from '@tanstack/react-table';
import { UploadDocumentsTableRow } from '../hooks';
import { FC, Fragment, MouseEvent, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { DataTableHeaderFilter, DataTableProgressBarRow, DataTableSortingIndicator } from '@repo/atomic-components';
import { useTableSpacerWidth } from '@/hooks';

// Column ids that drive a header-level filter. The urgent filter no longer
// lives on a column header — it moved to a quick-filter chip in the
// ActionsPanel — so only the three multi-select column filters remain.
type FilterOptionKey = 'assignedTo' | 'createdBy' | 'status';

const isFilterOptionKey = (key: string): key is FilterOptionKey => {
  return ['assignedTo', 'createdBy', 'status'].includes(key);
};

export interface UploadedDocumentsTableProps {
  table: Table<UploadDocumentsTableRow>;
  filterOptions: Record<string, string[]>;
  selectedFilterOptions: Record<string, string[]>;
  handleFilterChange: (columnId: string, values: string[]) => void;
  handleTableRowClick: (event: MouseEvent<HTMLTableRowElement>, caseId: string) => void;
}

export const CaseListTable: FC<UploadedDocumentsTableProps> = ({
  table,
  filterOptions,
  selectedFilterOptions,
  handleFilterChange,
  handleTableRowClick,
}) => {
  const [filterAnchor, setFilterAnchor] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const rows = table.getRowModel().rows;

  useTableSpacerWidth(containerRef, tableRef, [rows]);

  return (
    <div ref={containerRef} className="h-full w-full">
      <table
        ref={tableRef}
        className={classNames('min-w-full border-none', {
          'h-full': rows.length === 0,
        })}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={classNames('sticky top-0 z-10 h-12 max-h-12 min-h-12')}>
              {headerGroup.headers.map((header) => {
                return (
                  <Fragment key={header.id}>
                    {/* Header underline uses an inset `box-shadow` instead of
                        a `border-b` because Chrome / Safari drop the bottom
                        border on `<th>` cells once the parent `<tr>` enters
                        sticky positioning. `box-shadow` paints independently
                        of the table border model and stays visible through
                        the sticky transition. The colour matches the inter-
                        row dividers (`fletchers-gray-110` = #E1E1E0). */}
                    <th className="bg-fletchers-white px-2 py-1" style={{ boxShadow: 'inset 0 -1px 0 #E1E1E0' }}>
                      <div className="flex items-center justify-start">
                        <div
                          onClick={header.column.getToggleSortingHandler?.()}
                          className={classNames(
                            'flex items-center justify-center',
                            'text-fletchers-gray-175 text-[14px]/[20px] font-[600]',
                            {
                              'cursor-pointer': header.column.getCanSort?.(),
                            },
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </div>

                        {/* Sorting indicator */}
                        {header.column.getCanSort?.() && (
                          <div onClick={header.column.getToggleSortingHandler?.()} className="cursor-pointer">
                            <DataTableSortingIndicator
                              isSorted={header.column.getIsSorted?.()}
                              containerClassName="text-[#D9D9D9]"
                            />
                          </div>
                        )}

                        {/* Filter component (left of header text) */}
                        {header.column.getCanFilter?.() &&
                          isFilterOptionKey(header.column.id) &&
                          filterOptions[header.column.id] && (
                            <DataTableHeaderFilter
                              columnId={header.column.id}
                              filterOptions={filterOptions[header.column.id] || []}
                              currentFilterValue={selectedFilterOptions[header.column.id] || []}
                              setFilterValue={(value) => handleFilterChange(header.column.id, value)}
                              filterAnchor={filterAnchor}
                              setFilterAnchor={setFilterAnchor}
                            />
                          )}
                      </div>
                    </th>
                    {/* Spacer header cell — `data-spacer` marks it for the
                        `:not([data-spacer])` query that measures the real
                        cells. Width is driven by the JS-computed CSS var so
                        every spacer gets an equal pixel share of the
                        leftover width. Skip spacer for the last (actions)
                        column. */}
                    {header.id !== 'actions' && (
                      <th
                        aria-hidden
                        data-spacer
                        className="bg-fletchers-white"
                        style={{ width: 'var(--spacer-w, 100%)', boxShadow: 'inset 0 -1px 0 #E1E1E0' }}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  key={row.id}
                  className={classNames(
                    'group h-12 max-h-12 min-h-12',
                    // All rows are white; a 1px bottom border matching the
                    // card's outer border colour separates each row. The
                    // last row drops its border so it doesn't double-line
                    // against the wrapper's own bottom border.
                    'bg-fletchers-white border-fletchers-gray-110 border-b last:border-b-0',
                    'hover:bg-fletchers-gray-50 cursor-pointer',
                  )}
                  onClick={(e: MouseEvent<HTMLTableRowElement>) => handleTableRowClick(e, row.original.caseId)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Fragment key={cell.id}>
                      <td className={classNames('border-none px-2 py-1', 'text-[14px]/[20px] font-[400]')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext()) as React.ReactNode}
                      </td>
                      {/* Spacer body cell — same JS-computed `--spacer-w`
                          width as the header spacer above. Striped / hover
                          background comes from the parent <tr>. Skip spacer
                          for the last (actions) column. */}
                      {cell.column.id !== 'actions' && (
                        <td
                          aria-hidden
                          data-spacer
                          className="border-none"
                          style={{ width: 'var(--spacer-w, 100%)' }}
                        />
                      )}
                    </Fragment>
                  ))}
                </tr>

                {row.original.progressBars && (
                  <DataTableProgressBarRow
                    colSpan={table.getAllColumns().length * 2}
                    progressBars={row.original.progressBars}
                  />
                )}
              </Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={table.getAllColumns().length * 2}>
                <div
                  className={classNames(
                    'flex items-center justify-center',
                    'border-none px-2 py-1',
                    'text-[14px]/[20px] font-[400]',
                  )}
                >
                  No results found
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
